/**
 * motion-manifest/1 规范指纹（F0 修订 D1，I 06:45:14）。前端**不拿它验后端的 manifest_sha256**（D1：前端只比对、不重算）；
 * 用处只有两个：fixture 算真实指纹，以及播放器 key 里的本地语义摘要（后端指纹万一没盖住某个改动，比如只改了事件名，播放器照样重建）。
 *
 * 规范式：被哈希的对象是 {digest_version, header, clip}。
 * - header 取第 1 节全部字段；clip 取第 2 节全部字段，去掉 revision、manifest_sha256 与每帧的 url（签名地址轮换不算换版）。
 * - 比例量（anchor.x/y、hold_point.x/y、opaque_ratio）换成整数 floor(v × 1000000 + 0.5)；规范式里不出现小数。
 * - 字符串只许可打印 ASCII；布尔照写；空写 null；hold_point 缺省 null，grounded 缺省 true。
 * - frames 按 index 升序；events 按 (frame, name) 升序；interrupt_frames 升序去重。
 * - 键按 ASCII 字节序排序，分隔符 `,` 与 `:`、不留空白；对 ASCII 字节取 SHA-256，小写十六进制。
 * 不合规（非 ASCII、比例越界、该是整数的不是整数）就抛错：调用方把这段当坏段。
 * 固定向量 17 格见 docs/coordination/MOTION-MANIFEST-DIGEST-V1-VECTORS-I-2026-10-01.json，用例逐格复现。
 */
import type { MotionClip, MotionSet } from "./manifest";

export const MANIFEST_DIGEST_VERSION = "motion-manifest/1";

const PRINTABLE_ASCII = /^[\x20-\x7e]*$/;

export function isPrintableAscii(value: unknown): value is string {
  return typeof value === "string" && PRINTABLE_ASCII.test(value);
}

function text(value: unknown, what: string): string {
  if (!isPrintableAscii(value)) throw new Error(`${what} 不是可打印 ASCII`);
  return value;
}
function int(value: unknown, what: string): number {
  if (typeof value !== "number" || !Number.isInteger(value)) throw new Error(`${what} 不是整数`);
  return value;
}
function flag(value: unknown, what: string): boolean {
  if (typeof value !== "boolean") throw new Error(`${what} 不是真假`);
  return value;
}
function ppm(value: unknown, what: string): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0 || value > 1) throw new Error(`${what} 不在 0–1 之间`);
  return Math.floor(value * 1000000 + 0.5);
}

type Json = null | boolean | number | string | Json[] | { [key: string]: Json };

/** 键按 ASCII 字节序、紧凑写出（本规范里的键都是 ASCII，码点序即字节序）。 */
function serialize(value: Json): string {
  if (value === null || typeof value === "boolean" || typeof value === "number") return JSON.stringify(value);
  if (typeof value === "string") return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(serialize).join(",")}]`;
  const keys = Object.keys(value).sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
  return `{${keys.map((key) => `${JSON.stringify(key)}:${serialize(value[key])}`).join(",")}}`;
}

type DigestHeader = Pick<MotionSet, "pet_id" | "purpose" | "reference_version" | "identity_sha256" | "body_revision" | "style_version" | "canvas" | "anchor_rule" | "anchor" | "mirror_allowed" | "generation_method">;
type DigestClip = Pick<MotionClip, "clip_key" | "from_state" | "to_state" | "loop" | "facing" | "fallback_state" | "events" | "interrupt_frames" | "frames">;

export function canonicalManifest(header: DigestHeader, clip: DigestClip): string {
  const h: Json = {
    pet_id: text(header.pet_id, "pet_id"),
    purpose: text(header.purpose, "purpose"),
    reference_version: int(header.reference_version, "reference_version"),
    identity_sha256: text(header.identity_sha256, "identity_sha256"),
    body_revision: header.body_revision === null ? null : int(header.body_revision, "body_revision"),
    style_version: text(header.style_version, "style_version"),
    canvas: { width: int(header.canvas.width, "canvas.width"), height: int(header.canvas.height, "canvas.height") },
    anchor_rule: text(header.anchor_rule, "anchor_rule"),
    anchor: { x: ppm(header.anchor.x, "anchor.x"), y: ppm(header.anchor.y, "anchor.y"), measured: flag(header.anchor.measured, "anchor.measured") },
    mirror_allowed: flag(header.mirror_allowed, "mirror_allowed"),
    generation_method: text(header.generation_method, "generation_method"),
  };
  const frames: Json[] = [...clip.frames]
    .sort((a, b) => int(a.index, "index") - int(b.index, "index"))
    .map((f) => ({
      index: int(f.index, "index"),
      duration_ms: int(f.duration_ms, "duration_ms"),
      content_sha256: text(f.content_sha256, "content_sha256"),
      content_box: { left: int(f.content_box.left, "left"), top: int(f.content_box.top, "top"), right: int(f.content_box.right, "right"), bottom: int(f.content_box.bottom, "bottom") },
      anchor: { x: ppm(f.anchor.x, "frame.anchor.x"), y: ppm(f.anchor.y, "frame.anchor.y"), measured: flag(f.anchor.measured, "frame.anchor.measured") },
      has_alpha: flag(f.has_alpha, "has_alpha"),
      opaque_ratio: ppm(f.opaque_ratio, "opaque_ratio"),
      hold_point: f.hold_point == null ? null : { x: ppm(f.hold_point.x, "hold_point.x"), y: ppm(f.hold_point.y, "hold_point.y") },
      grounded: f.grounded === undefined ? true : flag(f.grounded, "grounded"),
    }));
  const events: Json[] = clip.events
    .map((e) => ({ frame: int(e.frame, "event.frame"), name: text(e.name, "event.name") }))
    .sort((a, b) => a.frame - b.frame || (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));
  const interrupts = [...new Set(clip.interrupt_frames.map((f) => int(f, "interrupt_frames")))].sort((a, b) => a - b);
  const c: Json = {
    clip_key: text(clip.clip_key, "clip_key"),
    from_state: text(clip.from_state, "from_state"),
    to_state: text(clip.to_state, "to_state"),
    loop: flag(clip.loop, "loop"),
    facing: text(clip.facing, "facing"),
    fallback_state: text(clip.fallback_state, "fallback_state"),
    events,
    interrupt_frames: interrupts,
    frames,
  };
  return serialize({ digest_version: MANIFEST_DIGEST_VERSION, header: h, clip: c });
}

/* ---------- SHA-256（同步、纯 TS；只处理 ASCII 串，规范式本来就只有 ASCII） ---------- */

const K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3,
  0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
  0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208,
  0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

export function sha256Ascii(input: string): string {
  if (!isPrintableAscii(input) && /[^\x00-\x7f]/.test(input)) throw new Error("只处理 ASCII");
  const length = input.length;
  const total = Math.ceil((length + 9) / 64) * 64;
  const bytes = new Uint8Array(total);
  for (let i = 0; i < length; i++) bytes[i] = input.charCodeAt(i);
  bytes[length] = 0x80;
  const bits = length * 8;
  const view = new DataView(bytes.buffer);
  view.setUint32(total - 8, Math.floor(bits / 0x100000000));
  view.setUint32(total - 4, bits >>> 0);
  const h = new Uint32Array([0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19]);
  const w = new Uint32Array(64);
  const rotr = (x: number, n: number) => (x >>> n) | (x << (32 - n));
  for (let offset = 0; offset < total; offset += 64) {
    for (let i = 0; i < 16; i++) w[i] = view.getUint32(offset + i * 4);
    for (let i = 16; i < 64; i++) {
      const s0 = rotr(w[i - 15], 7) ^ rotr(w[i - 15], 18) ^ (w[i - 15] >>> 3);
      const s1 = rotr(w[i - 2], 17) ^ rotr(w[i - 2], 19) ^ (w[i - 2] >>> 10);
      w[i] = (w[i - 16] + s0 + w[i - 7] + s1) >>> 0;
    }
    let [a, b, c, d, e, f, g, hh] = h;
    for (let i = 0; i < 64; i++) {
      const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const ch = (e & f) ^ (~e & g);
      const t1 = (hh + S1 + ch + K[i] + w[i]) >>> 0;
      const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (S0 + maj) >>> 0;
      hh = g;
      g = f;
      f = e;
      e = (d + t1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (t1 + t2) >>> 0;
    }
    h[0] = (h[0] + a) >>> 0;
    h[1] = (h[1] + b) >>> 0;
    h[2] = (h[2] + c) >>> 0;
    h[3] = (h[3] + d) >>> 0;
    h[4] = (h[4] + e) >>> 0;
    h[5] = (h[5] + f) >>> 0;
    h[6] = (h[6] + g) >>> 0;
    h[7] = (h[7] + hh) >>> 0;
  }
  return Array.from(h, (x) => x.toString(16).padStart(8, "0")).join("");
}

export function manifestDigest(header: DigestHeader, clip: DigestClip): string {
  return sha256Ascii(canonicalManifest(header, clip));
}
